/**
 * Plan Manager pre-enrollment custom flow pure logic.
 */

// --- from preEnrollmentListUtils.js ---

/**
 * Publication-status labels shown in the Status column.
 * @readonly
 * @enum {string}
 */
export const PreEnrollmentSiteStatus = Object.freeze({
    NOT_STARTED: "Not Started",
    PUBLISHED: "Published",
    UNPUBLISHED: "Unpublished",
});

/**
 * Tone tokens describing how a pre-enrollment site status should be visually emphasized.
 * Components map these tokens to their own CSS classes; the util stays free of styling.
 * @readonly
 * @enum {string}
 */
export const PreEnrollmentStatusTone = Object.freeze({
    NOT_STARTED: "not-started",
    PUBLISHED: "published",
    UNPUBLISHED: "unpublished",
});

/**
 * Row-action labels shown on the Start / Edit control.
 * @readonly
 * @enum {string}
 */
export const PreEnrollmentRowAction = Object.freeze({
    START: "Start",
    EDIT: "Edit",
});

/**
 * Normalizes an arbitrary value to a lowercase string for case-insensitive matching.
 * Does not trim, matching the custom-sites list search grammar.
 * @param {unknown} value
 * @returns {string}
 */
const toSearchableText = (value) =>
    value == null ? "" : String(value).toLowerCase();

/**
 * Filters custom-site rows by a free-text search against client name and
 * pre-enrollment URL name. Matching is case-insensitive and substring-based.
 * An empty criteria string returns the original list unchanged.
 *
 * @param {Array<Object>} customSites - In-memory custom-site rows to filter.
 * @param {string} searchCriteria - Raw user-typed search text.
 * @returns {Array<Object>} The subset of `customSites` matching the criteria.
 */
export const filterCustomSitesBySearchCriteria = (
    customSites,
    searchCriteria,
) => {
    if (!Array.isArray(customSites)) {
        return [];
    }

    const lowerSearchCriteria = toSearchableText(searchCriteria);
    if (lowerSearchCriteria === "") {
        return customSites;
    }

    return customSites.filter((client) => {
        const clientName = toSearchableText(client?.clientName);
        const oeUrlName = toSearchableText(client?.oeUrlName);

        return (
            clientName.includes(lowerSearchCriteria) ||
            oeUrlName.includes(lowerSearchCriteria)
        );
    });
};

/**
 * Compares two custom-site rows on a named field using case-insensitive
 * string order. Missing fields sort as empty strings.
 *
 * @param {Object} leftRow
 * @param {Object} rightRow
 * @param {string} field - Row property to compare (e.g. `clientName`).
 * @returns {number} Negative when `leftRow` sorts first, positive when `rightRow` sorts first, otherwise 0.
 */
export const compareCustomSiteRows = (leftRow, rightRow, field) => {
    const leftValue = toSearchableText(leftRow?.[field]);
    const rightValue = toSearchableText(rightRow?.[field]);

    return leftValue.localeCompare(rightValue);
};

/**
 * Returns a new array of custom-site rows ordered by `sortedField`.
 * When `sortedField` is empty, the original list is returned unchanged.
 *
 * @param {Array<Object>} customSites
 * @param {string | null} sortedField
 * @param {boolean} isSortDescending
 * @returns {Array<Object>}
 */
export const sortCustomSites = (customSites, sortedField, isSortDescending) => {
    if (!Array.isArray(customSites)) {
        return [];
    }

    if (!sortedField) {
        return customSites;
    }

    const orderedRows = [...customSites].sort((leftRow, rightRow) =>
        compareCustomSiteRows(leftRow, rightRow, sortedField),
    );

    if (isSortDescending) {
        orderedRows.reverse();
    }

    return orderedRows;
};

/**
 * Advances the custom-sites list three-state sort: unsorted → ascending → descending → unsorted.
 * Clicking a different field starts that field at ascending.
 *
 * @param {string | null} currentSortedField
 * @param {boolean} isSortDescending
 * @param {string} clickedField
 * @returns {{ sortedField: string | null, isSortDescending: boolean }}
 */
export const cycleCustomSiteListSort = (
    currentSortedField,
    isSortDescending,
    clickedField,
) => {
    if (currentSortedField === clickedField) {
        if (isSortDescending) {
            return { sortedField: null, isSortDescending: false };
        }

        return { sortedField: clickedField, isSortDescending: true };
    }

    return { sortedField: clickedField, isSortDescending: false };
};

/**
 * Formats a plain cell value, substituting an empty string for missing values.
 * @param {unknown} value
 * @returns {string}
 */
export const formatCustomSiteCellValue = (value) => value ?? "";

/**
 * Maps a site-status label to a visual tone token.
 * @param {unknown} status
 * @returns {string} A {@link PreEnrollmentStatusTone} value.
 */
export const getPreEnrollmentStatusTone = (status) => {
    switch (String(status)) {
        case PreEnrollmentSiteStatus.PUBLISHED:
            return PreEnrollmentStatusTone.PUBLISHED;
        case PreEnrollmentSiteStatus.UNPUBLISHED:
            return PreEnrollmentStatusTone.UNPUBLISHED;
        default:
            return PreEnrollmentStatusTone.NOT_STARTED;
    }
};

/**
 * Whether the row already has customization work underway.
 * "Not Started" (any casing) and missing status map to Start; every other status maps to Edit.
 *
 * @param {unknown} status
 * @returns {boolean}
 */
export const isPreEnrollmentCustomizationStarted = (status) => {
    const normalizedStatus = toSearchableText(status);
    if (normalizedStatus === "") {
        return false;
    }

    return normalizedStatus !== toSearchableText(PreEnrollmentSiteStatus.NOT_STARTED);
};

/**
 * Hover / accessible label for the row action control.
 * @param {unknown} status
 * @returns {string} A {@link PreEnrollmentRowAction} value.
 */
export const getPreEnrollmentRowActionLabel = (status) =>
    isPreEnrollmentCustomizationStarted(status)
        ? PreEnrollmentRowAction.EDIT
        : PreEnrollmentRowAction.START;

/**
 * Builds the search-results count label shown only when search is non-empty.
 * @param {number} resultCount
 * @returns {string}
 */
export const formatCustomSiteResultsFoundLabel = (resultCount) => {
    const count = Number.isFinite(resultCount) ? resultCount : 0;
    const resultWord = count === 1 ? "result" : "results";

    return `${count} ${resultWord} found`;
};

/**
 * Maps one getClientConfigs response item to a custom-sites list grid row.
 * @param {object | null | undefined} clientConfig
 * @returns {object}
 */
export const mapClientConfigToCustomSiteRow = (clientConfig) => {
    const clientName = clientConfig?.name ?? "";
    const clientId = clientConfig?.clientId ?? "";
    const oeUrlName = clientConfig?.preEnrollment?.siteURL ?? "N/A";

    let status = PreEnrollmentSiteStatus.NOT_STARTED;
    if (clientConfig?.preEnrollment?.hasCustomization) {
        status = clientConfig?.preEnrollment?.publish
            ? PreEnrollmentSiteStatus.PUBLISHED
            : PreEnrollmentSiteStatus.UNPUBLISHED;
    }

    return {
        id: `${clientId}#${clientName}`,
        clientName,
        clientId,
        oeUrlName,
        status,
    };
};

/**
 * Maps a getClientConfigs response body into sorted custom-sites list rows.
 * @param {Array<object> | null | undefined} clientConfigs
 * @returns {Array<object>}
 */
export const mapClientConfigsToCustomSiteRows = (clientConfigs) => {
    if (!Array.isArray(clientConfigs)) {
        return [];
    }

    return clientConfigs
        .map(mapClientConfigToCustomSiteRow)
        .sort((leftRow, rightRow) =>
            leftRow.clientName.localeCompare(rightRow.clientName),
        );
};

// --- from siteCustomizationUtils.js ---

/**
 * The four visual templates a client's open-enrollment site can use.
 * @readonly
 * @enum {string}
 */
export const TemplateId = Object.freeze({
    STANDARD: "template-1",
    TAILORED: "template-2",
    BASIC: "template-3",
    SLICE: "template-4",
});

/**
 * Template dropdown metadata in display order.
 * @readonly
 * @type {ReadonlyArray<{ id: string, title: string, isDefault?: boolean }>}
 */
export const TEMPLATE_INFO = Object.freeze([
    { id: TemplateId.STANDARD, title: "Template 1 - Standard", isDefault: true },
    { id: TemplateId.TAILORED, title: "Template 2 - Tailored" },
    { id: TemplateId.BASIC, title: "Template 3 - Basic" },
    { id: TemplateId.SLICE, title: "Template 4 - Slice Rebrand" },
]);

/**
 * Title removed from the dropdown when template 4 is suppressed by env flag.
 * @constant {string}
 */
export const SUPPRESSED_TEMPLATE_TITLE = "Template 4 - Slice Rebrand";

/**
 * Allowed image extensions for site customization uploads.
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const ALLOWED_IMAGE_EXTENSIONS = Object.freeze([
    "PNG",
    "JPG",
    "JPEG",
    "GIF",
    "SVG",
]);

/**
 * Default fallback image filenames shown when no custom file is set.
 * @readonly
 */
export const DEFAULT_IMAGE_FILE_NAMES = Object.freeze({
    logo: "default-logo.png",
    hero: "default-hero.png",
});

/**
 * Validates whether a file name has an allowed image extension.
 * @param {string} fileName
 * @param {ReadonlyArray<string>} [allowedExtensions=ALLOWED_IMAGE_EXTENSIONS]
 * @returns {boolean}
 */
export const isAllowedImageFile = (
    fileName,
    allowedExtensions = ALLOWED_IMAGE_EXTENSIONS,
) => {
    if (!fileName || typeof fileName !== "string") {
        return false;
    }

    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex === -1 || dotIndex === fileName.length - 1) {
        return false;
    }

    const ext = fileName.slice(dotIndex + 1).toUpperCase();
    return allowedExtensions.includes(ext);
};

/**
 * Reads a File or Blob into a Base64 Data URI string.
 * @param {Blob | File} file
 * @returns {Promise<string>}
 */
export const readImageAsDataUri = (file) =>
    new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error("No file provided"));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
    });

/**
 * Ordered list of enabled field keys per template (drives visibility gating).
 * @readonly
 * @type {Readonly<Record<string, ReadonlyArray<string>>>}
 */
export const TEMPLATE_FIELD_KEYS = Object.freeze({
    [TemplateId.STANDARD]: Object.freeze([
        "logo",
        "alreadyMember",
        "phone",
        "headline",
        "hero",
        "video",
        "heroButton",
        "withCentivo",
        "vpc",
        "centivoCare",
        "networkOfExcellence",
        "disclaimer",
        "testimonials",
    ]),
    [TemplateId.TAILORED]: Object.freeze([
        "logo",
        "alreadyMember",
        "phone",
        "headline",
        "hero",
        "video",
        "vpc",
        "centivoCare",
        "networkOfExcellence",
        "disclaimer",
        "testimonials",
    ]),
    [TemplateId.BASIC]: Object.freeze([
        "logo",
        "alreadyMember",
        "phone",
        "headline",
        "hero",
        "video",
        "vpc",
        "centivoCare",
        "disclaimer",
    ]),
    [TemplateId.SLICE]: Object.freeze([
        "logo",
        "alreadyMember",
        "phone",
        "headline",
        "externalLink",
        "description",
    ]),
});

/**
 * Default Centivo Care copy shown when the section is toggled on.
 * @readonly
 */
export const CENTIVO_CARE_DEFAULT = Object.freeze({
    title: "FREE virtual care when you need it, where you want it",
    body: "Centivo Care is a convenient alternative to in-person primary care — and all visits are entirely FREE! With Centivo Care, you have access to a primary care team with integrated behavioral health, plus access to a clinician 24/7/365 for urgent needs.",
    text: "Learn more",
    link: "https://www.centivocare.com/",
});

/**
 * Field placeholders (never persisted — hint text only).
 * @readonly
 */
export const PLACEHOLDERS = Object.freeze({
    headline: Object.freeze({
        [TemplateId.STANDARD]:
            "Searching for an affordable, easy-to-use health plan? You found it.",
        [TemplateId.TAILORED]:
            "Searching for an affordable, easy-to-use health plan? You found it.",
        [TemplateId.BASIC]:
            "Let's get started. Find a doctor or facility in your plan's network.",
        [TemplateId.SLICE]:
            "Let's get started. Find a doctor or facility in your plan's network.",
    }),
    description:
        "The Centivo Network is built around trusted, quality providers proven to deliver top results for their patients.",
    withCentivo: Object.freeze([
        "Affordable, predictable costs, so you know what you owe, if anything, before your visits.",
        "A primary care doctor to coordinate all your healthcare needs and refer you to trusted, in-network specialists and facilities.",
        "Dedicated hands-on member support by phone or through the app.",
        "A simple, straightforward plan with an easy-to-use app and member portal.",
    ]),
    networkOfExcellence:
        "The Centivo Network is built around trusted, quality providers proven to deliver top results for their patients.",
});

/**
 * Number of editable copy boxes rendered inside each accordion.
 * @readonly
 */
export const ACCORDION_BOX_COUNT = Object.freeze({
    withCentivo: 4,
    networkOfExcellence: 1,
});

/**
 * URL shape accepted for the optional hero video link.
 * @constant {RegExp}
 */
export const VIDEO_URL_PATTERN =
    /https:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()!@:%_+.~#?&/=]*)/;

/**
 * Phone value that is syntactically complete but semantically invalid.
 * @constant {string}
 */
export const PLACEHOLDER_PHONE = "000-000-0000";

/**
 * Resolves the effective template id, falling back to the standard template.
 * @param {string} [templateId] - Candidate template id.
 * @returns {string} A valid template id.
 */
export const resolveTemplateId = (templateId) =>
    TEMPLATE_FIELD_KEYS[templateId] ? templateId : TemplateId.STANDARD;

/**
 * Whether a given field is enabled for a template.
 * @param {string} templateId - Template id.
 * @param {string} field - Field key.
 * @returns {boolean} True when the field is part of the template.
 */
export const isFieldEnabled = (templateId, field) =>
    TEMPLATE_FIELD_KEYS[resolveTemplateId(templateId)].includes(field);

/**
 * Enabled-field lookup for a template, as a boolean map for rendering gates.
 * @param {string} templateId - Template id.
 * @returns {Record<string, boolean>} Map of field key to enabled flag.
 */
export const getEnabledFields = (templateId) =>
    TEMPLATE_FIELD_KEYS[resolveTemplateId(templateId)].reduce(
        (accumulator, field) => ({ ...accumulator, [field]: true }),
        {},
    );

/**
 * Fully-initialized customization config for a template. Every field is
 * present (React-controlled friendly); template-specific booleans differ.
 * @param {string} [templateId] - Template id; defaults to the standard template.
 * @returns {Object} Customization config with all fields initialized.
 */
export const getTemplateDefault = (templateId = TemplateId.STANDARD) => {
    const resolvedTemplateId = resolveTemplateId(templateId);

    return {
        selectedTemplate: resolvedTemplateId,
        logo: null,
        hero: null,
        alreadyMember: true,
        phoneEnabled: false,
        phone: "",
        headline: "",
        description: "",
        videoEnabled: false,
        video: "",
        heroButtonEnabled: false,
        heroButtonText: "",
        heroButtonUrl: "",
        withCentivo: Array.from(
            { length: ACCORDION_BOX_COUNT.withCentivo },
            () => "",
        ),
        vpc: true,
        centivoCare: { ...CENTIVO_CARE_DEFAULT },
        networkOfExcellence: Array.from(
            { length: ACCORDION_BOX_COUNT.networkOfExcellence },
            () => "",
        ),
        disclaimer: true,
        testimonials: resolvedTemplateId === TemplateId.STANDARD,
        externalLinkEnabled: false,
        externalLinkLabel: "",
        externalLinkUrl: "",
    };
};

/**
 * Structural equality for plain config values (primitives, arrays, objects).
 * @param {*} left - First value.
 * @param {*} right - Second value.
 * @returns {boolean} True when the two serialize identically.
 */
export const areValuesEqual = (left, right) =>
    JSON.stringify(left) === JSON.stringify(right);

/**
 * Switches templates while preserving any user-edited (non-default) values.
 * Fields left at their previous default are reset to the new template default.
 * @param {Object} currentData - Current customization config.
 * @param {string} nextTemplateId - Template id to switch to.
 * @returns {Object} Merged customization config for the next template.
 */
export const loadDefaultValuesForTemplate = (currentData, nextTemplateId) => {
    const resolvedNextTemplateId = resolveTemplateId(nextTemplateId);
    const nextDefaults = getTemplateDefault(resolvedNextTemplateId);
    const previousDefaults = getTemplateDefault(currentData?.selectedTemplate);

    const preservedValues = Object.keys(nextDefaults).reduce(
        (accumulator, field) => {
            if (field === "selectedTemplate") {
                return accumulator;
            }

            const currentValue = currentData?.[field];

            if (
                currentData?.selectedTemplate &&
                !isFieldEnabled(currentData.selectedTemplate, field)
            ) {
                return accumulator;
            }

            if (!isFieldEnabled(resolvedNextTemplateId, field)) {
                return accumulator;
            }

            if (currentValue == null) {
                return accumulator;
            }

            const wasEditedByUser = !areValuesEqual(
                currentValue,
                previousDefaults[field],
            );

            if (!wasEditedByUser) {
                return accumulator;
            }

            return { ...accumulator, [field]: currentValue };
        },
        {},
    );

    return {
        ...nextDefaults,
        ...preservedValues,
        selectedTemplate: resolvedNextTemplateId,
    };
};

/**
 * Template dropdown titles, optionally hiding the suppressed template.
 * @param {boolean} [isTemplate4Suppressed=false] - Whether to hide template 4.
 * @returns {Array<string>} Template titles in display order.
 */
export const getTemplateTitleOptions = (isTemplate4Suppressed = false) =>
    TEMPLATE_INFO.filter(
        (template) =>
            !isTemplate4Suppressed || template.title !== SUPPRESSED_TEMPLATE_TITLE,
    ).map((template) => template.title);

/**
 * Title for a template id.
 * @param {string} templateId - Template id.
 * @returns {string} Display title, or the default template's title.
 */
export const getTemplateTitle = (templateId) =>
    TEMPLATE_INFO.find((template) => template.id === templateId)?.title ??
    TEMPLATE_INFO[0].title;

/**
 * Template id for a dropdown title.
 * @param {string} title - Display title.
 * @returns {string} Matching template id, or the standard template id.
 */
export const getTemplateIdByTitle = (title) =>
    TEMPLATE_INFO.find((template) => template.title === title)?.id ??
    TemplateId.STANDARD;

/**
 * Validates the customization form, returning a flat error map.
 * `any` aggregates whether at least one field error is present.
 * @param {Object} data - Current customization config.
 * @returns {{
 *   phone: boolean,
 *   video: boolean,
 *   heroButtonLabel: boolean,
 *   heroButtonUrl: boolean,
 *   heroButton: boolean,
 *   externalLinkLabel: boolean,
 *   externalLinkUrl: boolean,
 *   externalLink: boolean,
 *   any: boolean,
 * }} Error map for the form.
 */
export const validateSiteCustomizationForm = (data = {}) => {
    const enabledFields = getEnabledFields(data.selectedTemplate);

    const phone = Boolean(
        enabledFields.phone &&
            data.phoneEnabled &&
            ((data.phone ?? "").length !== 12 || data.phone === PLACEHOLDER_PHONE),
    );

    const video = Boolean(
        enabledFields.video &&
            data.videoEnabled &&
            (!data.video || !VIDEO_URL_PATTERN.test(data.video)),
    );

    const heroButtonLabel = Boolean(
        data.heroButtonEnabled && !data.heroButtonText,
    );
    const heroButtonUrl = Boolean(data.heroButtonEnabled && !data.heroButtonUrl);
    const heroButton = heroButtonLabel && heroButtonUrl;

    const externalLinkLabel = Boolean(
        data.externalLinkEnabled && !data.externalLinkLabel,
    );
    const externalLinkUrl = Boolean(
        data.externalLinkEnabled && !data.externalLinkUrl,
    );
    const externalLink = externalLinkLabel && externalLinkUrl;

    const errors = {
        phone,
        video,
        heroButtonLabel,
        heroButtonUrl,
        heroButton,
        externalLinkLabel,
        externalLinkUrl,
        externalLink,
    };

    return {
        ...errors,
        any: Object.values(errors).some(Boolean),
    };
};

/**
 * Whether the customization form has no field errors.
 * @param {Object} data - Current customization config.
 * @returns {boolean} True when the form is valid.
 */
export const isSiteCustomizationFormValid = (data) =>
    !validateSiteCustomizationForm(data).any;

/**
 * Whether the Publish button must be disabled because an enabled optional
 * link (hero button on template 1, external link on template 4) is incomplete.
 * @param {Object} data - Current customization config.
 * @returns {boolean} True when publishing should be blocked.
 */
export const shouldDisablePublish = (data = {}) => {
    const isSliceExternalLinkIncomplete =
        data.selectedTemplate === TemplateId.SLICE &&
        Boolean(data.externalLinkEnabled) &&
        (!data.externalLinkLabel || !data.externalLinkUrl);

    const isStandardHeroButtonIncomplete =
        data.selectedTemplate === TemplateId.STANDARD &&
        Boolean(data.heroButtonEnabled) &&
        (!data.heroButtonText || !data.heroButtonUrl);

    return isSliceExternalLinkIncomplete || isStandardHeroButtonIncomplete;
};

/**
 * Copy for the publish/unpublish confirmation modal. The success `text` is
 * intentionally the inverse of the current state — it is shown only after the
 * action completes, by which point the state has flipped.
 * @param {boolean} isPublished - Whether the site is currently published.
 * @param {boolean} isSuccess - Whether the action has completed successfully.
 * @returns {{ title: string, text: string, confirmButtonText: string, secondaryButtonText: string }}
 */
/**
 * Backend `customContentConfig` keys mapped to frontend form field names.
 * @readonly
 */
export const BACKEND_TO_FRONTEND_FIELD_MAPPINGS = Object.freeze({
    FORMAT_PHONE: "phone",
    REACT_APP_HEADER_TEXT: "headline",
    REACT_APP_HERO_IMAGE: "hero",
    REACT_APP_LOGO: "logo",
    REACT_APP_NETWORK_OF_EXCELLENCE: "networkOfExcellence",
    REACT_APP_PHONE: "phone",
    SHOW_NUMBER_ON_MENU: "phoneEnabled",
    REACT_APP_TEMPLATE: "selectedTemplate",
    REACT_APP_WITH_CENTIVO_WILL_GET: "withCentivo",
    SHOW_ALREADY_MEMBER: "alreadyMember",
    SHOW_DISCLAIMER: "disclaimer",
    SHOW_TESTIMONIALS: "testimonials",
    SHOW_VPC: "vpc",
    CENTIVO_CARE: "centivoCare",
    REACT_APP_HERO_BTN: "heroButton",
    REACT_APP_EXTERNAL_LINK: "externalLink",
    REACT_APP_DESCRIPTION: "description",
    VIDEO_URL: "video",
    VIDEO_ENABLED: "videoEnabled",
});

/**
 * Frontend form field names mapped to backend `customContentConfig` wire keys.
 * Composite nests (`heroButton*`, `externalLink*`) are built in `formatForBackend`.
 * @readonly
 */
export const FRONTEND_TO_BACKEND_FIELD_MAPPINGS = Object.freeze({
    headline: "REACT_APP_HEADER_TEXT",
    hero: "REACT_APP_HERO_IMAGE",
    logo: "REACT_APP_LOGO",
    networkOfExcellence: "REACT_APP_NETWORK_OF_EXCELLENCE",
    phone: "REACT_APP_PHONE",
    phoneEnabled: "SHOW_NUMBER_ON_MENU",
    selectedTemplate: "REACT_APP_TEMPLATE",
    withCentivo: "REACT_APP_WITH_CENTIVO_WILL_GET",
    alreadyMember: "SHOW_ALREADY_MEMBER",
    disclaimer: "SHOW_DISCLAIMER",
    testimonials: "SHOW_TESTIMONIALS",
    vpc: "SHOW_VPC",
    centivoCare: "CENTIVO_CARE",
    description: "REACT_APP_DESCRIPTION",
    video: "VIDEO_URL",
    videoEnabled: "VIDEO_ENABLED",
});

/**
 * Capture-primary top-level and nested keys that every save body must cover.
 * @readonly
 */
export const REQUIRED_KEY_PATHS = Object.freeze([
    "CENTIVO_CARE",
    "REACT_APP_HERO_BTN",
    "REACT_APP_NETWORK_OF_EXCELLENCE",
    "REACT_APP_PHONE",
    "REACT_APP_TEMPLATE",
    "REACT_APP_WITH_CENTIVO_WILL_GET",
    "SHOW_ALREADY_MEMBER",
    "SHOW_DISCLAIMER",
    "SHOW_NUMBER_ON_MENU",
    "SHOW_TESTIMONIALS",
    "SHOW_VPC",
    "CENTIVO_CARE.title",
    "CENTIVO_CARE.body",
    "CENTIVO_CARE.text",
    "CENTIVO_CARE.link",
]);

/**
 * Coerces logo/hero form values into wire object payloads for the PUT body.
 * Backend handler requires `{ name: string, data: string }` or `null`.
 * @param {*} value - Form image value (object `{ name, data }`, string URL, or null).
 * @returns {{ name: string, data: string } | null} Wire image value.
 */
export const coerceDefaultImageWireValue = (value) => {
    if (value == null) {
        return null;
    }

    if (typeof value === "object") {
        if (value.data != null && String(value.data).trim() !== "") {
            return {
                name: typeof value.name === "string" && value.name.trim() !== ""
                    ? value.name
                    : "image.png",
                data: value.data,
            };
        }
        return null;
    }

    if (typeof value === "string" && value.trim() !== "") {
        const name = value.includes("/")
            ? value.slice(value.lastIndexOf("/") + 1)
            : value;
        return {
            name: name || "image.png",
            data: value,
        };
    }

    return null;
};

/**
 * Maps site-customization form state into a flat pre-enrollment-custom PUT body.
 * @param {Object} [formData] - Editor form state from `getTemplateDefault` / `useCustom`.
 * @returns {Object} Flat wire map of `REACT_APP_*` / `SHOW_*` / nested block keys.
 */
export const formatForBackend = (formData = {}) => {
    const resolvedTemplate = resolveTemplateId(formData.selectedTemplate);
    const fieldNames = TEMPLATE_FIELD_KEYS[resolvedTemplate];
    const templateOneUniverse = TEMPLATE_FIELD_KEYS[TemplateId.STANDARD];

    const config = {
        ...formData,
        centivoCare:
            formData.centivoCare && typeof formData.centivoCare === "object"
                ? { ...formData.centivoCare }
                : formData.centivoCare,
        withCentivo: Array.isArray(formData.withCentivo)
            ? [...formData.withCentivo]
            : formData.withCentivo,
        networkOfExcellence: Array.isArray(formData.networkOfExcellence)
            ? [...formData.networkOfExcellence]
            : formData.networkOfExcellence,
    };

    templateOneUniverse.forEach((key) => {
        if (!fieldNames.includes(key) && key !== "selectedTemplate") {
            config[key] = null;
        }
    });

    const formattedConfig = {};
    let hasBuiltHeroButton = false;
    let hasBuiltExternalLink = false;

    Object.keys(config).forEach((key) => {
        if (key.startsWith("heroButton")) {
            if (!hasBuiltHeroButton) {
                formattedConfig.REACT_APP_HERO_BTN = {
                    enabled: config.heroButtonEnabled,
                    text: config.heroButtonText,
                    link: config.heroButtonUrl,
                };
                hasBuiltHeroButton = true;
            }
            return;
        }

        if (key.startsWith("externalLink")) {
            if (!hasBuiltExternalLink) {
                formattedConfig.REACT_APP_EXTERNAL_LINK = {
                    enabled: config.externalLinkEnabled,
                    label: config.externalLinkLabel,
                    url: config.externalLinkUrl,
                };
                hasBuiltExternalLink = true;
            }
            return;
        }

        if (key === "withCentivo" && Array.isArray(config.withCentivo)) {
            formattedConfig.REACT_APP_WITH_CENTIVO_WILL_GET = config.withCentivo.map(
                (entry) =>
                    typeof entry !== "string" || entry.trim() === ""
                        ? undefined
                        : entry,
            );
            return;
        }

        const wireKey = FRONTEND_TO_BACKEND_FIELD_MAPPINGS[key];
        if (wireKey) {
            formattedConfig[wireKey] = config[key];
        }
    });

    if ("REACT_APP_LOGO" in formattedConfig) {
        formattedConfig.REACT_APP_LOGO = coerceDefaultImageWireValue(
            formattedConfig.REACT_APP_LOGO,
        );
    }

    if ("REACT_APP_HERO_IMAGE" in formattedConfig) {
        formattedConfig.REACT_APP_HERO_IMAGE = coerceDefaultImageWireValue(
            formattedConfig.REACT_APP_HERO_IMAGE,
        );
    }

    return formattedConfig;
};

/**
 * Coerces string boolean literals from the API into real booleans.
 * @param {*} value - Raw config value.
 * @returns {*} Coerced value.
 */
const coerceCustomContentConfigValue = (value) => {
    if (value === "true") {
        return true;
    }

    if (value === "false") {
        return false;
    }

    return value;
};

/**
 * Maps a backend `customContentConfig` object into partial site-customization
 * form fields (legacy `formatForFrontend` parity).
 * @param {Object} [customContentConfig] - Backend custom content config payload.
 * @returns {Object} Partial form state keyed by frontend field names.
 */
export const formatCustomContentConfigForFrontend = (customContentConfig) => {
    if (!customContentConfig || typeof customContentConfig !== "object") {
        return {};
    }

    const formFields = {};

    Object.keys(customContentConfig).forEach((key) => {
        let value = customContentConfig[key];

        if (key === "REACT_APP_HERO_BTN") {
            if (value && typeof value === "object") {
                if (value.enabled !== undefined && value.enabled !== null) {
                    formFields.heroButtonEnabled = Boolean(value.enabled);
                }
                if (value.text !== undefined && value.text !== null) {
                    formFields.heroButtonText = String(value.text);
                }
                if (value.link !== undefined && value.link !== null) {
                    formFields.heroButtonUrl = String(value.link);
                }
            }
            return;
        }

        if (key === "REACT_APP_EXTERNAL_LINK") {
            if (value && typeof value === "object") {
                if (value.enabled !== undefined && value.enabled !== null) {
                    formFields.externalLinkEnabled = Boolean(value.enabled);
                }
                if (value.label !== undefined && value.label !== null) {
                    formFields.externalLinkLabel = String(value.label);
                }
                if (value.url !== undefined && value.url !== null) {
                    formFields.externalLinkUrl = String(value.url);
                }
            }
            return;
        }

        if (key === "CENTIVO_CARE") {
            if (value && typeof value === "object" && Object.keys(value).length > 0) {
                formFields.centivoCare = {
                    title:
                        value.title != null
                            ? String(value.title)
                            : CENTIVO_CARE_DEFAULT.title,
                    body:
                        value.body != null
                            ? String(value.body)
                            : CENTIVO_CARE_DEFAULT.body,
                    text:
                        value.text != null
                            ? String(value.text)
                            : CENTIVO_CARE_DEFAULT.text,
                    link:
                        value.link != null
                            ? String(value.link)
                            : CENTIVO_CARE_DEFAULT.link,
                };
            }
            return;
        }

        if (key === "REACT_APP_WITH_CENTIVO_WILL_GET") {
            if (Array.isArray(value)) {
                formFields.withCentivo = Array.from(
                    { length: ACCORDION_BOX_COUNT.withCentivo },
                    (_, index) => (value[index] != null ? String(value[index]) : ""),
                );
            }
            return;
        }

        if (key === "REACT_APP_NETWORK_OF_EXCELLENCE") {
            if (Array.isArray(value)) {
                formFields.networkOfExcellence = Array.from(
                    { length: ACCORDION_BOX_COUNT.networkOfExcellence },
                    (_, index) => (value[index] != null ? String(value[index]) : ""),
                );
            }
            return;
        }

        value = coerceCustomContentConfigValue(value);

        if (
            key === "FORMAT_PHONE" &&
            formFields.phone !== undefined
        ) {
            return;
        }

        if (key === "REACT_APP_LOGO" || key === "REACT_APP_HERO_IMAGE") {
            const frontendKey = BACKEND_TO_FRONTEND_FIELD_MAPPINGS[key];
            if (value && typeof value === "object" && value.data != null) {
                formFields[frontendKey] = {
                    name: typeof value.name === "string" && value.name.trim() !== ""
                        ? value.name
                        : "image.png",
                    data: value.data,
                };
            } else if (typeof value === "string" && value.trim() !== "") {
                const name = value.includes("/")
                    ? value.slice(value.lastIndexOf("/") + 1)
                    : value;
                formFields[frontendKey] = {
                    name: name || "image.png",
                    data: value,
                };
            } else {
                formFields[frontendKey] = null;
            }
            return;
        }

        const frontendKey = BACKEND_TO_FRONTEND_FIELD_MAPPINGS[key];

        if (frontendKey) {
            if (value !== null && value !== undefined) {
                formFields[frontendKey] = value;
            }
        }
    });

    return formFields;
};

/**
 * Hydrates site-customization editor state from a client-config API response.
 * @param {Object} [clientConfig] - Client config payload from getClientConfig.
 * @returns {{ clientName: string, formData: Object, isPublished: boolean }}
 */
export const mapClientConfigToSiteCustomizationState = (clientConfig) => {
    if (!clientConfig || typeof clientConfig !== "object") {
        return {
            clientName: "",
            formData: getTemplateDefault(),
            isPublished: false,
        };
    }

    const customContentConfig = clientConfig.customContentConfig ?? {};
    const formattedFields =
        formatCustomContentConfigForFrontend(customContentConfig);

    return {
        clientName: clientConfig.name ?? "",
        formData: {
            ...getTemplateDefault(customContentConfig.REACT_APP_TEMPLATE),
            ...formattedFields,
        },
        isPublished: Boolean(clientConfig.preEnrollment?.publish),
    };
};

export const getPublishModalCopy = (isPublished, isSuccess) => {
    if (!isPublished) {
        return {
            title: "Confirm publishing",
            confirmButtonText: "Yes, publish",
            secondaryButtonText: "No, cancel",
            text: isSuccess
                ? "The pre-enrollment site has been unpublished."
                : "If you publish the pre-enrollment site it will be accessible to the public.",
        };
    }

    return {
        title: "Are you sure you want to unpublish?",
        confirmButtonText: "Yes, unpublish",
        secondaryButtonText: "No, cancel",
        text: isSuccess
            ? "The pre-enrollment site has been published."
            : "This should be used in case clients request any retroactive visual customizations or changes to the OE site URL.",
    };
};

/**
 * Default copy for the 4 'With Centivo, you'll get' cards.
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const DEFAULT_WCYG_CARDS = Object.freeze([
    "Affordable, predictable costs, so you know what you owe, if anything, before your visits.",
    "A primary care doctor to coordinate all your healthcare needs and guide you to trusted, in-network specialists and facilities.",
    "Dedicated hands-on member support by phone or through the Centivo app.",
    "A simple, straightforward plan with an easy-to-use app and member portal.",
]);

/**
 * Default paragraphs shown in the Network of Excellence section.
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const DEFAULT_NOE_PARAGRAPHS = Object.freeze([
    "The Centivo Network is built around trusted, quality providers proven to deliver top results for their patients.",
    "The Centivo Network includes all the doctors and specialists you may need, including virtual options.",
]);

/**
 * Default testimonials copy shown when testimonials are enabled.
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const DEFAULT_TESTIMONIALS = Object.freeze([
    "There were no unexpected costs and we knew everything up front, so there was no financial burden on us.",
    "I feel like I can take care of myself and my family without being in debt. Thank you!",
    "I feel like my health plan is my partner and not my enemy. In the past, I used to have to fight with them all the time.",
]);

/**
 * Provider search specialty options in display order.
 * @readonly
 * @type {ReadonlyArray<string>}
 */
export const PROVIDER_SPECIALTY_OPTIONS = Object.freeze([
    "Primary Care",
    "Acupuncture",
    "Allergy and Immunology",
    "Anesthesiology",
    "Audiology",
    "Behavioral Health",
    "Cardiology",
    "Chiropractic Care",
    "Colorectal Surgery",
    "Dentistry",
    "Dermatology",
    "Dialysis/Rehabilitation",
    "Emergency",
    "Endocrinology",
    "Gastroenterology and Hepatology",
    "General Surgery",
    "Genetics",
    "Home Health",
    "Hospital",
    "Hyperbaric Medicine",
    "Imaging/Radiology",
    "Infectious Disease",
    "Internal Medicine",
    "Labs",
    "Medical Supplies",
    "Nephrology",
    "Neurology",
    "Nurse Practitioners",
    "Nutrition",
    "Oncology and Hematology (Cancer and Blood)",
    "Orthopedics",
    "Other",
    "Otolaryngology",
    "Pediatrics",
    "Pharmacy",
    "Physical Medicine/Therapy",
    "Plastic and Reconstructive Surgery",
    "Podiatry",
    "Pulmonology",
    "Rheumatology",
    "Skilled Nursing Facility",
    "Speech Therapy",
    "Surgery Centers",
    "Transplant",
    "Urgent Care",
    "Urology",
    "Vascular Surgery",
    "Vision",
    "Women's Health",
]);

/**
 * Formats a raw phone string (e.g. `123-456-7890`) for display in the preview navbar as `(123) 456-7890`.
 * @param {unknown} phone
 * @returns {string}
 */
export const formatPhoneNumberForDisplay = (phone) => {
    if (!phone || typeof phone !== "string") {
        return "";
    }

    const parts = phone.split("-");
    if (parts.length === 3) {
        return `(${parts[0]}) ${parts[1]}-${parts[2]}`;
    }

    return phone;
};

/**
 * Resolves the preview headline, falling back to the template placeholder when empty.
 * @param {unknown} headline
 * @param {string} [templateId]
 * @returns {string}
 */
export const resolveEffectiveHeadline = (headline, templateId) => {
    if (typeof headline === "string" && headline.trim() !== "") {
        return headline;
    }

    const resolvedTemplate = resolveTemplateId(templateId);
    return (
        PLACEHOLDERS.headline[resolvedTemplate] ??
        PLACEHOLDERS.headline[TemplateId.STANDARD]
    );
};

/**
 * Resolves the preview description, falling back to the placeholder when empty.
 * @param {unknown} description
 * @param {string} [templateId]
 * @returns {string}
 */
export const resolveEffectiveDescription = (description, templateId) => {
    if (typeof description === "string" && description.trim() !== "") {
        return description;
    }

    return PLACEHOLDERS.description;
};

/**
 * Resolves the 4 'With Centivo, you'll get' cards, filling empty slots from defaults.
 * @param {Array<string> | null | undefined} withCentivo
 * @returns {Array<string>}
 */
export const resolveEffectiveWcygCards = (withCentivo) => {
    return DEFAULT_WCYG_CARDS.map((defaultCard, index) => {
        const customValue = withCentivo?.[index];
        return typeof customValue === "string" && customValue.trim() !== ""
            ? customValue
            : defaultCard;
    });
};

/**
 * Resolves Network of Excellence paragraphs from custom copy or defaults.
 * Splits multi-line paragraphs into individual paragraph strings.
 * @param {Array<string> | null | undefined} networkOfExcellence
 * @returns {Array<string>}
 */
export const resolveEffectiveNoeParagraphs = (networkOfExcellence) => {
    if (
        !Array.isArray(networkOfExcellence) ||
        networkOfExcellence.every((text) => !text || String(text).trim() === "")
    ) {
        return [...DEFAULT_NOE_PARAGRAPHS];
    }

    return networkOfExcellence
        .flatMap((paragraph) =>
            typeof paragraph === "string" ? paragraph.split("\n") : [],
        )
        .filter((line) => line.trim() !== "");
};

/**
 * Resolves Centivo Care block copy, falling back each field to default if empty.
 * @param {Object | null | undefined} centivoCare
 * @returns {{ title: string, body: string, text: string, link: string }}
 */
export const resolveEffectiveCentivoCare = (centivoCare) => {
    const raw =
        centivoCare && typeof centivoCare === "object" ? centivoCare : {};

    return {
        title:
            typeof raw.title === "string" && raw.title.trim() !== ""
                ? raw.title
                : CENTIVO_CARE_DEFAULT.title,
        body:
            typeof raw.body === "string" && raw.body.trim() !== ""
                ? raw.body
                : CENTIVO_CARE_DEFAULT.body,
        text:
            typeof raw.text === "string" && raw.text.trim() !== ""
                ? raw.text
                : CENTIVO_CARE_DEFAULT.text,
        link:
            typeof raw.link === "string" && raw.link.trim() !== ""
                ? raw.link
                : CENTIVO_CARE_DEFAULT.link,
    };
};

/**
 * Resolves an image source for preview (data URI, URL, or fallback asset source).
 * @param {unknown} imageValue - Form value (`{ data, name }`, string, or null).
 * @param {string} fallbackSource - Fallback asset path/URL.
 * @returns {string} Effective image src.
 */
export const resolvePreviewImageSource = (imageValue, fallbackSource) => {
    if (!imageValue) {
        return fallbackSource;
    }

    if (typeof imageValue === "object" && imageValue !== null) {
        return imageValue.data || fallbackSource;
    }

    if (typeof imageValue === "string" && imageValue.trim() !== "") {
        return imageValue;
    }

    return fallbackSource;
};
